/* components/ui/Popover.jsx — лёгкий поповер/меню с якорем.
   Позиционируется относительно триггера (bottom-start по умолчанию),
   закрывается по клику-вне и ESC. Не перекрывает навигацию (z-popover).
   Используется для меню «…» в строках, user-menu, фильтров.

   Esc при открытом меню — ТОЛЬКО меню (как у Select): слушатель в фазе захвата на document
   гасит событие (stopPropagation), закрывает меню и СРАЗУ возвращает фокус на триггер. Во
   всплытии Esc первой ловила ловушка фокуса Drawer/Modal, внутри которого стоит триггер: пока
   фокус ещё на триггере (перевод на первый пункт — через setTimeout), Esc закрывал всё окно
   вместе с правками (kitchen-ux D1b, меню «Ещё» редактора ТТК). Закрытое меню Esc не трогает —
   следующий Esc достаётся окну.
   Свой Esc меню — только когда фокус в его панели, на триггере или на body (круг 3). Фокус в
   окне, которое пункт открыл, не закрыв меню («Сменить пространство» → сторож несохранённого),
   или в списке Select внутри меню (портал списка — вне панели) — Esc их, меню не трогаем. Фокус
   на ДРУГОМ раскрытом элементе в панели (aria-expanded="true": Select без поиска внутри меню) —
   тоже его: он ближе, закроется сам, следующий Esc — меню.

   children-функция получает close(opts): close() — просто закрыть (фокус не трогается);
   close({ restore: true }) — сначала СИНХРОННО фокус на триггер, затем закрыть. Так пункт,
   который открывает подтверждение (Modal), оставляет триггер прежним фокусом окна, и после
   «Отмена» / Esc фокус возвращается на него, а не на body (пункт к тому времени удалён).

   role / label (kitchen-ux C4, a11y): по умолчанию панель — меню (role="menu", у триггера
   aria-haspopup="menu"). Панель без пунктов menuitem (штамп и кнопка в «Ещё» ряда сегментов
   «Базы POS») объявляет себя тем, что она есть: role="dialog" | "group" с именем label; у
   триггера aria-haspopup той же роли (для group — "true": такой роли у haspopup нет). */

import React from 'react';
import ReactDOM from 'react-dom';
import { Icon } from './Icon.jsx';
import { getOverlayRoot } from '../../lib/overlay.jsx';

  const { useState, useRef, useEffect, useLayoutEffect, cloneElement } = React;

  const HASPOPUP = { menu: 'menu', dialog: 'dialog', listbox: 'listbox', tree: 'tree', grid: 'grid' };

  function Popover({ trigger, children, placement = 'bottom-start', align, gap = 6, width, role = 'menu', label }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    const panelRef = useRef(null);

    const place = () => {
      const t = triggerRef.current; if (!t) return;
      const r = t.getBoundingClientRect();
      const pw = panelRef.current ? panelRef.current.offsetWidth : (width || 220);
      const ph = panelRef.current ? panelRef.current.offsetHeight : 160;
      let top, left;
      const [side, al] = placement.split('-');
      if (side === 'bottom') top = r.bottom + gap;
      else if (side === 'top') top = r.top - ph - gap;
      else top = r.bottom + gap;
      if (al === 'end') left = r.right - pw;
      else left = r.left;
      // клампим в вьюпорт
      left = Math.max(8, Math.min(left, window.innerWidth - pw - 8));
      if (top + ph > window.innerHeight - 8) top = Math.max(8, r.top - ph - gap);
      setPos({ top, left });
    };

    useLayoutEffect(() => { if (open) place(); /* eslint-disable-next-line */ }, [open]);

    // menuitem'ы панели (для клавиатурной навигации стрелками)
    const items = () => panelRef.current
      ? Array.from(panelRef.current.querySelectorAll('[role="menuitem"],button:not([disabled])')).filter((el) => el.offsetParent !== null)
      : [];

    // при открытии — фокус на первый пункт; при закрытии — возврат на триггер
    useEffect(() => {
      if (!open) return;
      const id = setTimeout(() => { const it = items(); if (it[0]) it[0].focus(); }, 0);
      return () => clearTimeout(id);
    }, [open]);

    // фокус на триггер — синхронно: подтверждение, открытое следом, запомнит его прежним фокусом
    const focusTrigger = () => {
      const t = triggerRef.current;
      if (t && typeof t.focus === 'function') { try { t.focus(); } catch (e) { /* noop */ } }
    };
    const close = (opts) => {
      if (opts && opts.restore === true) focusTrigger();
      setOpen(false);
    };

    useEffect(() => {
      if (!open) return;
      const onDoc = (e) => {
        if (panelRef.current && panelRef.current.contains(e.target)) return;
        if (triggerRef.current && triggerRef.current.contains(e.target)) return;
        setOpen(false);
      };
      // Esc — первым, в фазе захвата: иначе его съест ловушка фокуса окна (Drawer/Modal:
      // stopPropagation + onClose), внутри которого стоит триггер. Но только свой (см. шапку).
      const ownsEscape = (t) => {
        if (!t || t === document || t === document.body) return true;
        const trigger = triggerRef.current;
        if (trigger && typeof trigger.contains === 'function' && trigger.contains(t)) return true;
        const panel = panelRef.current;
        if (!panel || typeof panel.contains !== 'function' || !panel.contains(t)) return false;
        return !(typeof t.getAttribute === 'function' && t.getAttribute('aria-expanded') === 'true');
      };
      const onEsc = (e) => {
        if (e.key !== 'Escape' || !ownsEscape(e.target)) return;
        e.stopPropagation();
        close({ restore: true });
      };
      const onKey = (e) => {
        const it = items();
        if (!it.length) return;
        const cur = it.indexOf(document.activeElement);
        if (e.key === 'ArrowDown') { e.preventDefault(); it[(cur + 1 + it.length) % it.length].focus(); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); it[(cur - 1 + it.length) % it.length].focus(); }
        else if (e.key === 'Home') { e.preventDefault(); it[0].focus(); }
        else if (e.key === 'End') { e.preventDefault(); it[it.length - 1].focus(); }
      };
      const onScroll = () => place();
      document.addEventListener('mousedown', onDoc);
      document.addEventListener('keydown', onEsc, true);
      document.addEventListener('keydown', onKey);
      window.addEventListener('resize', onScroll, true);
      window.addEventListener('scroll', onScroll, true);
      return () => {
        document.removeEventListener('mousedown', onDoc);
        document.removeEventListener('keydown', onEsc, true);
        document.removeEventListener('keydown', onKey);
        window.removeEventListener('resize', onScroll, true);
        window.removeEventListener('scroll', onScroll, true);
      };
    }, [open]);

    const triggerEl = cloneElement(trigger, {
      ref: triggerRef,
      onClick: (e) => { trigger.props.onClick && trigger.props.onClick(e); setOpen((o) => !o); },
      'aria-expanded': open,
      'aria-haspopup': HASPOPUP[role] || 'true',
    });

    return (
      <>
        {triggerEl}
        {open && ReactDOM.createPortal(
          <div className="popover" ref={panelRef} role={role} aria-label={label || undefined}
               style={{ top: pos.top, left: pos.left, width, zIndex: 'var(--z-popover)' }}>
            {typeof children === 'function' ? children({ close }) : children}
          </div>,
          getOverlayRoot()
        )}
      </>
    );
  }

  // удобный пункт меню
  function PopoverItem({ icon, children, onClick, danger, close }) {
    return (
      <button className={'popover__item' + (danger ? ' popover__item--danger' : '')} role="menuitem"
              onClick={(e) => { onClick && onClick(e); close && close(); }}>
        {icon && <Icon name={icon} size={16} />}
        <span className="grow">{children}</span>
      </button>
    );
  }

export { Popover };
export { PopoverItem };
